// Funnel tracking. POSTs anonymous step events to `@solblox/api`'s
// POST /api/track so per-launch unit economics (visit → connect → enter
// → first_touch joined from koth_touches) can be reconstructed.
//
// Two surfaces:
//   - `useTrack()` for React call sites that already live under
//     <LaunchProvider> (LaunchProvider mount, wallet adapter onConnect).
//   - `trackEvent(step, extras)` for non-React call sites (colyseus.ts
//     after joinOrCreate). Reads the launch slug from `?launch=` and the
//     visitor id from localStorage so it needs no context.
//
// Identity model:
//   - visitorId is a UUID stored in localStorage under "solblox.vid".
//     Survives reload/disconnect; per-browser, not per-wallet. Bouncers
//     stay anonymous forever.
//   - wallet is only attached starting at wallet_connect.
//
// Per-step dedupe: React StrictMode mounts effects twice, and most call
// sites here are inside effects. An in-module Set of `${visitorId}:${step}`
// keys suppresses the second mount. The server still rate-limits as
// defense in depth.

import { useCallback, useMemo } from "react";
import { useLaunchTheme } from "./LaunchProvider";

const VISITOR_KEY = "solblox.vid";
const VISITOR_RE = /^[A-Za-z0-9_-]{8,64}$/;
const LAUNCH_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

const apiBase = (): string => {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_API_URL ?? "http://localhost:3030";
};

export type TrackStep = "landing" | "wallet_connect" | "room_enter";

export interface TrackExtras {
  wallet?: string | null;
  themeId?: string | null;
  referrer?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
}

const firedKeys = new Set<string>();

function getOrCreateVisitorId(): string {
  if (typeof window === "undefined") return "ssr-noop-visitor";
  try {
    const existing = window.localStorage.getItem(VISITOR_KEY);
    if (existing && VISITOR_RE.test(existing)) return existing;
    const fresh =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID().replace(/-/g, "")
        : Math.random().toString(36).slice(2) +
          Math.random().toString(36).slice(2);
    window.localStorage.setItem(VISITOR_KEY, fresh);
    return fresh;
  } catch {
    return (
      "mem-" +
      Math.random().toString(36).slice(2) +
      Math.random().toString(36).slice(2)
    );
  }
}

/** Mirror of LaunchProvider's readLaunchId; we don't import it to avoid a
 *  React dependency from the non-hook surface. Slug-safe chars only. */
function readLaunchIdFromUrl(): string {
  if (typeof window === "undefined") return "main";
  const raw = new URL(window.location.href).searchParams.get("launch");
  if (raw === null) return "main";
  if (!LAUNCH_ID_RE.test(raw)) return "main";
  return raw;
}

function readUtm(): {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
} {
  if (typeof window === "undefined") {
    return { utmSource: null, utmMedium: null, utmCampaign: null };
  }
  const sp = new URL(window.location.href).searchParams;
  return {
    utmSource: sp.get("utm_source"),
    utmMedium: sp.get("utm_medium"),
    utmCampaign: sp.get("utm_campaign"),
  };
}

function postEvent(launchId: string, step: TrackStep, extras: TrackExtras | undefined): void {
  const visitorId = getOrCreateVisitorId();
  const key = `${visitorId}:${step}`;
  if (firedKeys.has(key)) return;
  firedKeys.add(key);

  const utm = readUtm();
  const body = {
    launch_id: launchId,
    visitor_id: visitorId,
    step,
    theme_id: extras?.themeId ?? launchId,
    referrer:
      extras?.referrer ??
      (typeof document !== "undefined" ? document.referrer || null : null),
    wallet: extras?.wallet ?? null,
    utm_source: extras?.utmSource ?? utm.utmSource,
    utm_medium: extras?.utmMedium ?? utm.utmMedium,
    utm_campaign: extras?.utmCampaign ?? utm.utmCampaign,
  };

  // keepalive: lets the request survive a tab close on landing →
  // navigation flows. Body is tiny (<1KB), well under the keepalive cap.
  void fetch(`${apiBase()}/api/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {
    // Network failure is non-fatal; un-dedupe so a retry can land if
    // the same step is re-attempted later in the session.
    firedKeys.delete(key);
  });
}

/** Plain function for non-React call sites (e.g. colyseus.ts). Reads
 *  launchId from `?launch=` and visitorId from localStorage. */
export function trackEvent(step: TrackStep, extras?: TrackExtras): void {
  postEvent(readLaunchIdFromUrl(), step, extras);
}

export interface UseTrack {
  /** Fire-and-forget. Dedupes per (visitor, step) within the page load. */
  track: (step: TrackStep, extras?: TrackExtras) => void;
  /** Stable visitor id so callers can attach it to other systems. */
  visitorId: string;
}

/** React hook variant: uses the LaunchProvider context for launch id so
 *  callers under <LaunchProvider overrideTheme=…> get the right slug in
 *  tests. */
export function useTrack(): UseTrack {
  const visitorId = useMemo(getOrCreateVisitorId, []);
  const launch = useLaunchTheme();
  const track = useCallback(
    (step: TrackStep, extras?: TrackExtras): void => {
      postEvent(launch.id, step, extras);
    },
    [launch.id],
  );
  return { track, visitorId };
}
